import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import type { Permission } from './permissions';

interface CanProps {
  readonly permission?: Permission;
  readonly allOf?: readonly Permission[];
  readonly anyOf?: readonly Permission[];
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Declarative permission gate. UI hides controls a user can't act on.
 * Backend MUST still enforce — never trust the client.
 *
 * Usage:
 *   <Can permission="payments.refund"><RefundButton /></Can>
 *   <Can anyOf={['terminals.view', 'estate.view']}>...</Can>
 */
export function Can({ permission, allOf, anyOf, fallback = null, children }: CanProps): ReactNode {
  const { hasPermission } = useAuth();
  const passes =
    (permission === undefined || hasPermission(permission)) &&
    (allOf === undefined || allOf.every(hasPermission)) &&
    (anyOf === undefined || anyOf.some(hasPermission));
  return passes ? children : fallback;
}
